import { MAX_DUPLICATE_KEYS, canonicalDuplicateKeys } from "@/lib/guests/duplicateKeys";
import type { CompanionScope } from "@/types/enums/CompanionScope";
import { Filter } from "@/types/Filter";
import { ConditionGroup } from "@/types/EventRule";
import { EmailOptInBreakdown } from "@/types/EmailOptInBreakdown";
import { Guest } from "@/types/Guest";
import { ImportBatch } from "@/types/ImportBatch";
import { BulkSelection, BulkEditableUpdates } from "@/types/BulkSelection";
import { GuestDeleteImpact } from "@/types/GuestDeleteImpact";
import type { DeleteGuestsResult } from "@/types/GuestTrash";
import { learnGuestTrashEnabled } from "@/lib/guests/guestTrashFeature";
import { handleErrorServer, isServerErrorResponse, ServerError } from "@/lib/api/errors/ErrorHandler";
import {
  checkInOut,
  createGuestsBatch,
  deleteGuestsBatch,
  deleteGuestsBulk,
  getGuestDeleteImpact,
  updateGuestsByFilter,
  getOrderedCompanionsWithGuests as getOrderedCompanionsWithGuestsApi,
  getPaginatedGuests,
  getPaginatedGuestsToBeReviewed,
  getReviewedGuestCount,
  getValidGuestCount,
  updateGuests as updateGuestsApi,
  type BatchUpdateAck,
  countCompanions as countCompanionsApi,
  findDuplicatesByField as findDuplicatesByFieldApi,
  previewImportDuplicates as previewImportDuplicatesApi,
  DedupePreviewResponse,
  getGuestsByIds as getGuestsByIdsApi,
  getCompanions as getCompanionsApi,
  createPending as createPendingnApi,
  createCompanionRequest as createCompanionRequestApi,
  getPaginatedGuestsInversed,
  batchCheckInOut as batchCheckInOutApi,
  getEmailOptInBreakdown as getEmailOptInBreakdownApi,
  getGuestImports as getGuestImportsApi,
  getGuestGroupCounts as getGuestGroupCountsApi,
  type GuestGroupCount,
} from "@/lib/api/clients/GuestClient";
import { isValidObjectId } from "@/lib/utils/input-validator";
import { sanitizeGuest } from "@/lib/utils/sanitizer";

// Activity-rollup fields (seating / appointments / email / website) are
// denormalized scalars+arrays on the guest doc, stamped server-side. They are
// offered as guest-list columns (field-groups.ts) but were NOT requested here,
// so any such column rendered EMPTY regardless of the underlying data — e.g.
// "Raum" (seatRoomIds) showed nothing even for a seated guest. They are all in
// the backend ALLOWED_GUEST_FIELDS projection allowlist, so requesting them is
// safe. Note: `seatInfo` (Sitzplatz) is the guest's PRIMARY seat — lowest
// furniture id, then seat index. It used to be an arbitrary $limit-1 collapse
// that disagreed with both the seating rail and the hover card; all three now
// share that one tie-break. It is still ONE seat: `seatCount` says how many
// there really are and `seatRoomIds` names the rooms.
// `emailUndeliverable(At)` (PB-TH-7) joined when the two became guest-list
// columns; the detail popup's refetch below carried them long before.
const GUEST_ROLLUP_FIELDS =
  "apptStatuses,apptCount,nextApptAt,hasOpenRequest,apptOfferingIds,apptTreatmentIds,apptLaneIds,seatStatuses,seatCount,seatRoomIds,lastEmailedAt,lastEmailStatus,lastEmailOpenedAt,lastEmailClickedAt,emailedTemplateIds,emailedSendNames,emailUndeliverable,emailUndeliverableAt,portalVisitedAt,websiteLoginAt";

// ⚠ `eventId` here is INERT, and knowing why saves the next reader the trip:
// this endpoint answers with `GuestDTO`, which declares no `eventId` field, and
// the document→DTO mapper only fills declared fields — so the key is projected
// out of Mongo and then dropped on the way to the wire. Asking for it costs
// nothing and becomes correct the day the DTO gains the field.
//
// The popup therefore CANNOT take its event id from the row. It resolves it from
// the surrounding GuestsProvider / EventContext instead (PGuestDetails'
// `resolvedEventId`), which is what actually keeps its 20s poll, focus refetch,
// post-send refresh and check-in refresh alive. Do not "simplify" that back to
// `guest.eventId`.
//
// `acceptanceState` is NOT decoration here: it is the only input to the guest
// list's `guestType` edit gate (`isEditableFieldGuest` in GuestsRows). The gate
// fails OPEN on an undefined value, so dropping the key from this projection
// does not break a render — it silently re-opens type conversion on undecided
// guests. PENDING rows do reach this list (the backend read is
// `acceptanceState ∈ [SYSTEM, ACCEPTED] OR state == WAITLISTED`).
// `loginToken` is DERIVED server-side from username+code (main-backend
// `GuestLoginToken`) and only exists in a response that ASKED for it. The
// list projection is this constant, NOT the active view's columns — so a
// column the catalogue offers but this string omits renders empty for every
// guest, forever, exactly like the rollups above did.
export const GUEST_FIELDS = "guestId,eventId,state,acceptanceState,gender,title,firstName,lastName,company,jobTitle,address,address2,zipCode,city,country,email,phone,mobile,language,username,code,loginToken,archived,createdAt,updatedAt,emailOptIn,emailOptInAt,emailOptInSource,smsOptIn,smsOptInAt,smsOptInSource,additionalFields,checkedIn,checkedInAt,numCompanions,origin,importId,speakerId,sponsorId,companions,additionalFields,guestType,mainGuest,mainGuestDTO,appointments,seatInfo," + GUEST_ROLLUP_FIELDS;

/**
 * Projection for re-reading the ONE guest a detail popup shows: the popup's
 * poll/focus refetch, the standalone popup's only fetch, and the companion
 * fetches all share it.
 *
 * Lives here rather than next to the popup so every consumer can import it
 * without an import cycle (PGuestDetails → GuestDetailBody → GuestCompanions),
 * and so it stays visibly in sync with GUEST_FIELDS above — three hand-written
 * near-copies had already drifted apart, each missing different keys.
 *
 * ⚠ Every field a popup CELL reads must be in here. A display-only layout cell
 * whose key is missing renders empty and no test or type will notice.
 */
// PB-TH-7: emailUndeliverable/-At ride this projection because the guest-detail
// popup renders its bounce notice from the guest it was handed. Left out, the
// field arrives undefined, the notice never appears, and the feature is invisible
// while looking fully wired — the same half-wiring that let the flag sit in the
// database for a day with no reader at all. PB-RCS-1: `emailOptInAt` rode for
// the consent block's legacy line; the block left the popup (29.10.2026) and
// the pair stays requested (pinned in guestProjections.test).
export const GUEST_REFETCH_FIELDS =
  "guestId,eventId,guestType,mainGuest,mainGuestDTO,state,acceptanceState,gender,title,firstName,lastName,company,jobTitle,address,address2,zipCode,city,country,email,phone,mobile,language,username,code,archived,createdAt,updatedAt,emailOptIn,emailOptInAt,emailOptInSource,smsOptIn,smsOptInAt,smsOptInSource,emailUndeliverable,emailUndeliverableAt,additionalFields,checkedIn,checkedInAt,numCompanions,origin,importId,seatInfo,seatCount,seatRoomIds,seatStatuses";

export async function getGuests(
  eventId: string,
  searchTerm: string,
  page: number,
  size: number,
  filter: Filter[],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
  whitelist?: string[],
  blacklist?: string[],
  opts?: { conditions?: ConditionGroup; archived?: boolean; groupBy?: string },
): Promise<Guest[] | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (size < 1 || size > 100) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    const response = await getPaginatedGuests(
      GUEST_FIELDS,
      searchTerm,
      page,
      size,
      filter,
      sortBy,
      sortOrder,
      eventId,
      whitelist,
      blacklist,
      opts,
    );

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * One page of guestIds for the CURRENT list criteria — `fields=guestId`
 * projection over the same paginated endpoint the guest list uses, so an
 * all-matching CRM transfer resolves exactly the rows the user is looking at
 * (search + flat filters OR condition tree + archived). 100/page (endpoint
 * cap); callers page until a short page.
 */
export async function getGuestIdsPage(
  eventId: string,
  searchTerm: string,
  page: number,
  filter: Filter[],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
  opts?: { conditions?: ConditionGroup; archived?: boolean },
): Promise<string[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    const response = await getPaginatedGuests(
      "guestId",
      searchTerm,
      page,
      100,
      filter,
      sortBy,
      sortOrder,
      eventId,
      undefined,
      undefined,
      opts,
    );

    if (!Array.isArray(response)) throw new Error("unknown");

    return response
      .map((guest) => guest.guestId)
      .filter((id): id is string => !!id);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getGuestsInversed(
  eventId: string,
  searchTerm: string,
  page: number,
  size: number,
  filter: Filter[],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
  whitelist?: string[],
  blacklist?: string[],
  opts?: {
    conditions?: ConditionGroup;
    archived?: boolean;
    allRecipients?: boolean;
  },
): Promise<Guest[] | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (size < 1 || size > 100) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    const response = await getPaginatedGuestsInversed(
      GUEST_FIELDS,
      searchTerm,
      page,
      size,
      filter,
      sortBy,
      sortOrder,
      eventId,
      whitelist,
      blacklist,
      opts,
    );

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * One page of the guests that share the composite `fieldNames` key with at
 * least one other active guest (AND over every key — see
 * `lib/guests/duplicateKeys`). The list is sent as the backend's canonical
 * form; the size and shape are checked here so a bad call fails as
 * `validationError` before it leaves the browser (the backend's own codes,
 * `duplicateFieldTooMany` etc., are for callers that bypass this handler).
 */
export async function findDuplicatesByField(
  eventId: string,
  fieldNames: readonly string[],
  page: number = 0,
  pageSize: number = 10,
) {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");
    if (pageSize < 1 || pageSize > 100) throw new Error("validationError");
    if (!Array.isArray(fieldNames) || fieldNames.length < 1 || fieldNames.length > MAX_DUPLICATE_KEYS)
      throw new Error("validationError");
    if (fieldNames.some((f) => typeof f !== "string" || f.trim() === "" || f.includes(",")))
      throw new Error("validationError");
    if (new Set(fieldNames).size !== fieldNames.length) throw new Error("validationError");

    return await findDuplicatesByFieldApi(
      eventId,
      canonicalDuplicateKeys(fieldNames),
      "guestId,state,gender,title,firstName,lastName,company,jobTitle,address,address2,zipCode,city,country,email,phone,mobile,language,username,code,archived,createdAt,updatedAt,emailOptIn,emailOptInAt,emailOptInSource,smsOptIn,smsOptInAt,smsOptInSource,additionalFields,checkedIn,checkedInAt,numCompanions,companions",
      page,
      pageSize,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function previewImportDuplicates(
  eventId: string,
  conditions: unknown,
  rows: Record<string, unknown>[],
): Promise<DedupePreviewResponse | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    return await previewImportDuplicatesApi(eventId, conditions, rows);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getOrderedCompanionsWithGuests(
  eventId: string,
  archived: boolean,
  page: number,
  size: number,
  filters: Filter[],
  searchTerm: string = "",
): Promise<Guest[] | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (size < 1 || size > 100) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    return await getOrderedCompanionsWithGuestsApi(
      eventId,
      archived,
      "guestId,state,acceptanceState,gender,title,firstName,lastName,company,jobTitle,address,address2,zipCode,city,country,email,phone,mobile,language,username,code,archived,createdAt,updatedAt,emailOptIn,emailOptInAt,emailOptInSource,smsOptIn,smsOptInAt,smsOptInSource,additionalFields,checkedIn,checkedInAt,numCompanions,companions,guestType,mainGuest,mainGuestDTO",
      filters,
      page,
      size,
      searchTerm,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function countCompanions(
  eventId: string,
  filter: Filter[],
  archived: boolean,
  searchTerm: string = "",
): Promise<number | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await countCompanionsApi(eventId, archived, filter, searchTerm);
  } catch (error) {
    console.error("Error in countCompanions:", error);
    return handleErrorServer(error);
  }
}

export async function getGuestsToBeReviewed(
  eventId: string,
  page: number,
  size: number,
  filter: Filter[],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
  archived: boolean,
  searchTerm: string = "",
): Promise<Guest[] | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (size < 1 || size > 100) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    filter.push({ key: "guestType", operator: "=", value: "mainGuest" });

    const response = await getPaginatedGuestsToBeReviewed(
      eventId,
      page,
      size,
      filter,
      sortBy,
      sortOrder,
      "guestId,state,acceptanceState,gender,title,firstName,lastName,company,jobTitle,address,address2,zipCode,city,country,email,phone,mobile,language,username,code,archived,createdAt,updatedAt,emailOptIn,emailOptInAt,emailOptInSource,smsOptIn,smsOptInAt,smsOptInSource,additionalFields,checkedIn,checkedInAt,numCompanions,companions,additionalFields",
      archived,
      searchTerm,
    );

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getGuestsCount(
  archived: boolean,
  filters: Filter[],
  eventId: string,
  searchTerm: string = "",
  whitelist?: string[],
  blacklist?: string[],
  requireEmail?: boolean,
  conditions?: ConditionGroup,
  // "mainGuest" → count PARTIES instead of people (the grouped list's page
  // denominator). Anything else is ignored by the count lane.
  groupBy?: string,
): Promise<number | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await getValidGuestCount(
      eventId,
      filters,
      archived,
      searchTerm,
      whitelist,
      blacklist,
      requireEmail,
      conditions,
      groupBy,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Group-header counts for the column-grouped guest list. Fails SOFT: a header
 * without a number is fine, a wrong number is not.
 */
export async function getGuestGroupCounts(
  eventId: string,
  filters: Filter[],
  archived: boolean,
  groupBy: string,
  searchTerm: string = "",
  conditions?: ConditionGroup,
): Promise<GuestGroupCount[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    return await getGuestGroupCountsApi(
      eventId,
      filters,
      archived,
      groupBy,
      searchTerm,
      conditions,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getGuestsCountToBeReviewed(
  archived: boolean,
  filters: Filter[],
  eventId: string,
  searchTerm: string = "",
): Promise<number | ServerError> {
  try {
    return await getReviewedGuestCount(
      eventId,
      filters,
      archived,
      searchTerm,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Recipient consent breakdown for the pre-send opt-in warning (counts restricted
 * to recipients WITH an email). Arg order mirrors getGuestsCount; maps to the
 * GuestClient call. Fails soft via ServerError so the hook can degrade gracefully.
 */
export async function getEmailOptInBreakdown(
  archived: boolean,
  filters: Filter[],
  eventId: string,
  searchTerm: string = "",
  whitelist?: string[],
  blacklist?: string[],
  conditions?: ConditionGroup,
): Promise<EmailOptInBreakdown | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await getEmailOptInBreakdownApi(
      eventId,
      filters,
      archived,
      searchTerm,
      whitelist,
      blacklist,
      conditions,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * List the event's CSV import batches (newest first) for the guest-list
 * "Import" source filter. Fails soft to a ServerError so the picker can degrade
 * to "no import batches" without breaking the rest of the filter UI.
 */
export async function getGuestImports(
  eventId: string,
): Promise<ImportBatch[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    const response = await getGuestImportsApi(eventId);

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createGuests(
  eventId: string | string[] | undefined,
  guests: Guest[],
): Promise<string[] | ServerError> {
  try {

    if (!isValidObjectId(eventId as string)) throw new Error("validationError");

    const sanitizedGuests = guests.map((guest) => {
      const sanitized = sanitizeGuest(guest);
      sanitized.eventId = eventId as string;
      return sanitized;
    });

    return await createGuestsBatch(
      eventId as string,
      sanitizedGuests,
    );
  } catch (error) {
    console.error("Error in createGuests:", error);
    return handleErrorServer(error);
  }
}

export async function updateGuests(
  eventId: string,
  guests: Guest[],
): Promise<BatchUpdateAck | void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await updateGuestsApi(eventId, guests);
  } catch (error) {
    console.error("Error in updateGuests:", error);
    return handleErrorServer(error);
  }
}

/**
 * Dispatches a BulkSelection to the right endpoint:
 *   ids → /update-batch  (accepts per-row Guest partials)
 *   filter → /batch-update-by-filter (server resolves IDs, then same per-row path)
 *
 * `updates` is narrow on purpose: only the fields the backend whitelists for bulk
 * mutation. Keeping the type strict at this boundary prevents future callers from
 * piping in arbitrary Guest fields.
 */
async function applyGuestUpdate(
  eventId: string,
  selection: BulkSelection,
  updates: BulkEditableUpdates,
): Promise<void | ServerError> {
  if (selection.mode === "ids") {
    if (selection.ids.length === 0) return;
    // Per-row endpoint takes Guest partials. additionalFields are passed
    // straight through; the backend merges them onto the existing doc.
    const result = await updateGuestsApi(
      eventId,
      selection.ids.map((id) => ({ guestId: id, ...(updates as Partial<Guest>) })),
    );
    // The bulk lane cannot announce anything: `BulkEditableUpdates` carries no
    // `acceptanceState`, so no lifecycle trigger can be derived and the server
    // answers with an empty body. Dropping the ack here keeps this function's
    // contract exactly as it was rather than widening it for a value that
    // structurally never arrives.
    return isServerErrorResponse(result) ? result : undefined;
  }
  // v3: pipe every defined key through to the wire payload. Enum-typed
  // fields (state, gender) serialize to their string label; everything else
  // passes through as-is. Going generic here means adding a new field to
  // `BulkEditableUpdates` doesn't require touching this builder.
  const filterUpdates: Record<string, unknown> = {};
  for (const k of Object.keys(updates) as (keyof BulkEditableUpdates)[]) {
    const v = updates[k];
    if (v === undefined) continue;
    if (k === "state" || k === "gender") {
      filterUpdates[k] = String(v);
    } else {
      filterUpdates[k] = v;
    }
  }
  await updateGuestsByFilter(
    eventId,
    {
      filters: selection.filters,
      excludeIds: selection.excludeIds,
      search: selection.search,
      archived: selection.archived,
      selectAll: selection.selectAll,
      updates: filterUpdates,
    },
  );
}

export async function archiveGuests(
  eventId: string,
  selection: BulkSelection,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (selection.mode === "ids" && selection.ids.length === 0) return;
    return await applyGuestUpdate(eventId, selection, { archived: true });
  } catch (error) {
    console.error("Error in archiveGuests:", error);
    return handleErrorServer(error);
  }
}

export async function unarchiveGuests(
  eventId: string,
  selection: BulkSelection,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (selection.mode === "ids" && selection.ids.length === 0) return;
    return await applyGuestUpdate(eventId, selection, { archived: false });
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Generic bulk edit for guests. Backed by the same endpoints as the archive /
 * unarchive shims (per-row `/update-batch` for ids-mode; filter-aware
 * `/batch-update-by-filter` for filter-mode). The {@code updates} whitelist is
 * enforced at compile time by {@link BulkEditableUpdates} and at runtime by
 * the backend's `BulkUpdateFields` validator.
 */
export async function bulkEditGuests(
  eventId: string,
  selection: BulkSelection,
  updates: BulkEditableUpdates,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (selection.mode === "ids" && selection.ids.length === 0) return;
    // Generic predicate so adding fields to BulkEditableUpdates doesn't
    // require touching this gate. additionalFields counts only when the map
    // actually has keys (an empty map shouldn't pass).
    const hasAnyUpdate = Object.entries(updates).some(([k, v]) => {
      if (v === undefined) return false;
      if (k === "additionalFields") {
        return v !== null && typeof v === "object" && Object.keys(v).length > 0;
      }
      return true;
    });
    if (!hasAnyUpdate) throw new Error("validationError");
    return await applyGuestUpdate(eventId, selection, updates);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Delete (W6: move to the trash, or `permanent: true` to skip it). Answers the
 * door's `{mode, count, trashBatchId, purgeAt}` so the caller can arm the 15 s
 * undo; `null` from a backend that answers with an empty body (no trash yet)
 * or for an empty id list.
 */
export async function deleteGuests(
  eventId: string,
  selection: BulkSelection,
  options?: { permanent?: boolean },
): Promise<DeleteGuestsResult | null | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    // Only a true travels: an old backend's request validator may reject an
    // unknown key, and `false` says nothing the absence does not.
    const permanent = options?.permanent ? { permanent: true as const } : {};
    let result: DeleteGuestsResult | null;
    if (selection.mode === "ids") {
      if (selection.ids.length === 0) return null;
      result = await deleteGuestsBulk(eventId, { ids: selection.ids, ...permanent });
    } else {
      result = await deleteGuestsBulk(
        eventId,
        {
          filters: selection.filters,
          excludeIds: selection.excludeIds,
          search: selection.search,
          archived: selection.archived,
          selectAll: selection.selectAll,
          ...permanent,
        },
      );
    }
    // A TRASH answer is the one proof this event has the trash ON — the
    // sidebar's Papierkorb entry keys on it (see guestTrashFeature).
    if (result?.mode === "TRASH") learnGuestTrashEnabled(eventId, true);
    return result;
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Delete-impact preview for the confirmation dialog. Best-effort: returns
 * null on any failure so the dialog still works (it just falls back to the
 * bare count) — the preview must never block the delete flow.
 */
export async function fetchGuestDeleteImpact(
  eventId: string,
  selection: BulkSelection,
): Promise<GuestDeleteImpact | null> {
  try {
    if (!isValidObjectId(eventId)) return null;
    if (selection.mode === "ids") {
      if (selection.ids.length === 0) return null;
      return rememberTrashFlag(eventId, await getGuestDeleteImpact(eventId, { ids: selection.ids }));
    }
    return rememberTrashFlag(eventId, await getGuestDeleteImpact(eventId, {
      filters: selection.filters,
      excludeIds: selection.excludeIds,
      search: selection.search,
      archived: selection.archived,
      selectAll: selection.selectAll,
    }));
  } catch {
    return null;
  }
}

/** The impact is where the backend says whether the trash is on — the sidebar learns it from here. */
function rememberTrashFlag(eventId: string, impact: GuestDeleteImpact): GuestDeleteImpact {
  if (impact && typeof impact === "object" && impact.trash && typeof impact.trash.enabled === "boolean") {
    learnGuestTrashEnabled(eventId, impact.trash.enabled);
  }
  return impact;
}

export async function checkInGuest(
  eventId: string,
  guestId: string,
  checkIn: boolean,
  performedByType: string = "Admin Suite",
): Promise<{ warning?: string } | void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(guestId)) throw new Error("validationError");


    return await checkInOut(eventId, guestId, checkIn, performedByType);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function batchCheckInGuests(
  eventId: string,
  checkIn: boolean,
  filters: Filter[],
  performedByType: string = "Admin Suite",
): Promise<{ processed: number; skipped?: number; capacityReached?: boolean } | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");


    return await batchCheckInOutApi(eventId, checkIn, filters, performedByType);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getGuestsByIds(
  eventId: string,
  guestIds: string[],
  searchTerm: string = "",
  filters: Filter[] = [],
  fields: string,
  page: number = 0,
  pageSize: number = 10,
): Promise<Guest[] | ServerError> {
  try {
    if (!guestIds.length || guestIds.length === 0) return [];


    // Validate eventId
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    // Validate guestIds array
    if (!Array.isArray(guestIds) || guestIds.length === 0) {
      throw new Error("validationError");
    }

    // Validate each guestId in the array
    for (const guestId of guestIds) {
      if (!isValidObjectId(guestId)) {
        throw new Error("validationError");
      }
    }

    // Validate pagination parameters
    if (pageSize < 0 || pageSize > 20000) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    const response = await getGuestsByIdsApi(
      eventId,
      guestIds,
      searchTerm,
      fields,
      filters,
      page,
      pageSize,
    );

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    console.error("Error in getGuestsByIds:", error);
    return handleErrorServer(error);
  }
}

export async function getCompanions(
  eventId: string,
  guestId: string,
  fields: string = "guestId,firstName,lastName,email,acceptanceState,guestType",
  /**
   * WHICH companions — see `CompanionScope`. Omitted keeps the set this route
   * has always answered; the guest popup names one explicitly so its list and
   * the guest list can no longer disagree about who is in the party.
   */
  scope?: CompanionScope,
): Promise<Guest[] | ServerError> {
  try {

    // Validate eventId
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    // Validate guestId
    if (!isValidObjectId(guestId)) throw new Error("validationError");

    const response = await getCompanionsApi(eventId, guestId, fields, scope);

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    console.error("Error in getCompanions:", error);
    return handleErrorServer(error);
  }
}

export async function createPending(
  eventId: string,
  guest: Guest,
): Promise<{ guestIds: string[]; guest?: Guest; outcome?: string } | ServerError> {
  try {

    // Validate eventId
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    const sanitized = sanitizeGuest(guest);
    sanitized.eventId = eventId as string;

    const response = await createPendingnApi(eventId, sanitized);

    if (!response) throw new Error("unknown");

    return response;
  } catch (error) {
    console.error("Error in createPending:", error);
    return handleErrorServer(error);
  }
}

export async function createCompanionRequest(
  eventId: string,
  guest: Guest,
): Promise<{ guestId: string; guest?: Guest } | ServerError> {
  // P3 (UX9): return the full hydrated shape so the CompanionsProvider
  // optimistic insert uses the server-returned row instead of the
  // partial form values (which left "—" in system columns until the
  // next refresh).
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");

    const sanitized = sanitizeGuest(guest);
    sanitized.eventId = eventId as string;

    const response = await createCompanionRequestApi(eventId, sanitized);

    if (!response || !response.guestId) throw new Error("unknown");

    return response;
  } catch (error) {
    console.error("Error in createCompanionRequest:", error);
    return handleErrorServer(error);
  }
}
